import type {
  WorkflowFile,
  WorkflowEntity,
  WorkflowEntityProperty,
  WorkflowExecution,
  WorkflowBuilderState,
  WorkflowRelationship,
  WorkflowRelationshipMapping,
  UnstructuredRelationship,
  LinkNodesStep,
  LinkRule,
  EnrichRule,
  CypherClassifierStep,
} from '@/interfaces/workflows.interface';
import {
  getDefaultAgentTypeForFileType,
  linkingDefaults,
  getDefaults,
  extractionAgentTypeFromApi,
  AGENT_TYPES,
  SERVICES,
  LINKING_STRATEGY_TYPES,
  EMBEDDINGS_API_TYPES,
  embeddingModels,
} from '@/config/models.config';

export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

export const validateEntity = (entity: WorkflowEntity): boolean => {
  return entity.name.trim().length > 0 && entity.properties.length > 0;
};

/**
 * Validates if an entity has valid location properties
 * For structured: Location properties must have both latitudeColumn and longitudeColumn set
 * For unstructured: Location properties must have both latitudeProperty and longitudeProperty set
 * (also accepts legacy latitudeColumn/longitudeColumn format for backward compatibility)
 */
export const validateLocationProperties = (entity: WorkflowEntity): boolean => {
  const locationProperties = entity.properties.filter(
    (prop) => prop.type === 'location'
  );

  // If no location properties, it's valid
  if (locationProperties.length === 0) {
    return true;
  }

  // All location properties must have both latitude and longitude (unless static)
  // For unstructured: use latProperty/lonProperty (new format) or latColumn/lonColumn (legacy format)
  // For structured: use latColumn/lonColumn
  // Values must be non-empty strings (truthy check already rejects empty strings, but be explicit)
  return locationProperties.every((prop) => {
    if (prop.isStatic) return true;
    if (entity.entityType === 'unstructured') {
      // Accept both new format (latitudeProperty/longitudeProperty) and legacy format
      // (latitudeColumn/longitudeColumn) for backward compatibility
      // Values must be non-empty strings
      // Use != null to check for both null and undefined, and optional chaining for safety
      const hasNewFormat =
        prop.latitudeProperty != null &&
        prop.latitudeProperty.trim() !== '' &&
        prop.longitudeProperty != null &&
        prop.longitudeProperty.trim() !== '';
      const hasLegacyFormat =
        prop.latitudeColumn != null &&
        prop.latitudeColumn.trim() !== '' &&
        prop.longitudeColumn != null &&
        prop.longitudeColumn.trim() !== '';
      return hasNewFormat || hasLegacyFormat;
    }
    return (
      prop.latitudeColumn != null &&
      prop.latitudeColumn.trim() !== '' &&
      prop.longitudeColumn != null &&
      prop.longitudeColumn.trim() !== ''
    );
  });
};

export const validateEntityProperty = (
  property: WorkflowEntityProperty
): boolean => {
  if (property.isStatic) {
    return (
      property.name.trim().length > 0 && property.staticValue !== undefined
    );
  }

  // Location type requires both latitudeColumn and longitudeColumn
  // Values must be defined and non-empty strings
  // Use != null to check for both null and undefined to prevent runtime errors
  if (property.type === 'location') {
    return (
      property.name.trim().length > 0 &&
      property.latitudeColumn != null &&
      property.latitudeColumn.trim() !== '' &&
      property.longitudeColumn != null &&
      property.longitudeColumn.trim() !== ''
    );
  }

  return property.name.trim().length > 0 && property.sourceField !== undefined;
};

export const validateUnstructuredEntityProperty = (
  property: WorkflowEntityProperty
): boolean => {
  if (property.isStatic) {
    return (
      property.name.trim().length > 0 && property.staticValue !== undefined
    );
  }

  // Location type requires both latitudeProperty and longitudeProperty for unstructured
  // Values must be defined and non-empty strings
  // Also accepts legacy latitudeColumn/longitudeColumn format for backward compatibility
  // Use != null to check for both null and undefined to prevent runtime errors
  if (property.type === 'location') {
    // Check new format (latitudeProperty/longitudeProperty)
    const hasNewFormat =
      property.latitudeProperty != null &&
      property.latitudeProperty.trim() !== '' &&
      property.longitudeProperty != null &&
      property.longitudeProperty.trim() !== '';
    const hasLegacyFormat =
      property.latitudeColumn != null &&
      property.latitudeColumn.trim() !== '' &&
      property.longitudeColumn != null &&
      property.longitudeColumn.trim() !== '';
    return (
      property.name.trim().length > 0 && (hasNewFormat || hasLegacyFormat)
    );
  }
  // For unstructured entities, only require name - sourceField is optional
  return property.name.trim().length > 0;
};

type EntityPropertyPair = { entityLabel: string; propertyName: string };

/**
 * Shared logic: build entity updates to set hasIndex on the given (entityLabel, propertyName) pairs.
 * Skips when property already has the target value; merges multiple properties per entity.
 */
function applyPropertyIndexUpdates(
  entities: WorkflowEntity[],
  pairs: EntityPropertyPair[],
  hasIndexValue: boolean
): Array<{ entityId: string; updates: Partial<WorkflowEntity> }> {
  const result: Array<{ entityId: string; updates: Partial<WorkflowEntity> }> = [];
  const indexByEntityId = new Map<string, number>();

  const apply = (entityLabel: string, propertyName: string) => {
    if (!entityLabel || !propertyName.trim()) return;
    const entity = entities.find((e) => e.name === entityLabel);
    if (!entity) return;
    const prop = entity.properties.find((p) => p.name === propertyName);
    if (!prop || prop.hasIndex === hasIndexValue) return;

    const idx = indexByEntityId.get(entity.id);
    if (idx !== undefined) {
      const entry = result[idx];
      entry.updates.properties = (entry.updates.properties || entity.properties).map((p) =>
        p.name === propertyName ? { ...p, hasIndex: hasIndexValue } : p
      );
      return;
    }
    indexByEntityId.set(entity.id, result.length);
    result.push({
      entityId: entity.id,
      updates: {
        properties: entity.properties.map((p) =>
          p.name === propertyName ? { ...p, hasIndex: hasIndexValue } : p
        ),
      },
    });
  };

  pairs.forEach(({ entityLabel, propertyName }) => apply(entityLabel, propertyName));
  return result;
}

/**
 * Returns entity updates to set hasIndex on properties used by a relationship mapping
 * (startNodeProperty and endNodeProperty). Used when adding/updating a relationship
 * so that match properties are indexed without the user having to check hasIndex manually.
 */
export const getEntityUpdatesForRelationshipMappingIndexes = (
  mapping: WorkflowRelationshipMapping,
  entities: WorkflowEntity[]
): Array<{ entityId: string; updates: Partial<WorkflowEntity> }> => {
  return applyPropertyIndexUpdates(
    entities,
    [
      { entityLabel: mapping.startLabel, propertyName: mapping.startNodeProperty },
      { entityLabel: mapping.endLabel, propertyName: mapping.endNodeProperty },
    ],
    true
  );
};

/**
 * Returns entity updates to clear hasIndex on properties that were used only by the
 * removed mapping. Used when removing a relationship mapping so that the index is
 * removed from the JSON configuration when no relationship uses that property.
 */
export const getEntityUpdatesToClearIndexWhenMappingRemoved = (
  removedMapping: WorkflowRelationshipMapping,
  remainingMappings: WorkflowRelationshipMapping[],
  entities: WorkflowEntity[]
): Array<{ entityId: string; updates: Partial<WorkflowEntity> }> => {
  const stillUsed = (entityLabel: string, propertyName: string) =>
    remainingMappings.some(
      (m) =>
        (m.startLabel === entityLabel && m.startNodeProperty === propertyName) ||
        (m.endLabel === entityLabel && m.endNodeProperty === propertyName)
    );

  const result: Array<{ entityId: string; updates: Partial<WorkflowEntity> }> = [];
  const indexByEntityId = new Map<string, number>();

  const clearIfUnused = (entityLabel: string, propertyName: string) => {
    if (!entityLabel || !propertyName.trim()) return;
    if (stillUsed(entityLabel, propertyName)) return;
    const entity = entities.find((e) => e.name === entityLabel);
    if (!entity) return;
    const prop = entity.properties.find((p) => p.name === propertyName);
    if (!prop || prop.hasIndex !== true) return;

    const idx = indexByEntityId.get(entity.id);
    if (idx !== undefined) {
      const entry = result[idx];
      entry.updates.properties = (entry.updates.properties || entity.properties).map(
        (p) => (p.name === propertyName ? { ...p, hasIndex: false } : p)
      );
      return;
    }
    indexByEntityId.set(entity.id, result.length);
    result.push({
      entityId: entity.id,
      updates: {
        properties: entity.properties.map((p) =>
          p.name === propertyName ? { ...p, hasIndex: false } : p
        ),
      },
    });
  };

  clearIfUnused(removedMapping.startLabel, removedMapping.startNodeProperty);
  clearIfUnused(removedMapping.endLabel, removedMapping.endNodeProperty);
  return result;
};

/**
 * Returns entity updates to set hasIndex on properties used by a LinkNodes configuration
 * (source_prop / destination_prop in link rules). Used when adding/updating a LinkNodes
 * configuration so that match properties are indexed without the user having to set hasIndex.
 */
export const getEntityUpdatesForLinkNodesConfigurationIndexes = (
  configuration: LinkNodesStep,
  entities: WorkflowEntity[]
): Array<{ entityId: string; updates: Partial<WorkflowEntity> }> => {
  const sourceLabel = configuration.link?.source?.label;
  const destinationLabel = configuration.link?.destination?.label;
  const linkRules: LinkRule[] = configuration.link?.linkRules ?? [];

  const pairs: EntityPropertyPair[] = [];
  if (sourceLabel && destinationLabel) {
    linkRules.forEach((rule) => {
      (rule.match?.rules ?? []).forEach((match) => {
        pairs.push(
          { entityLabel: sourceLabel, propertyName: match.source_prop },
          { entityLabel: destinationLabel, propertyName: match.destination_prop }
        );
      });
    });
  }

  return applyPropertyIndexUpdates(entities, pairs, true);
};

export const createNewEntity = (
  name: string = 'New Entity',
  entityType: 'structured' | 'unstructured' = 'structured',
  fileType?: 'pdf' | 'txt' | 'json' | 'directory',
  selectedFile?: WorkflowFile
): WorkflowEntity => {
  const entity: WorkflowEntity = {
    id: generateId(),
    name,
    properties: [],
    sources: [],
    isComplete: false,
    color: '#3B82F6',
    entityType,
  };

  // Add unstructured-specific fields
  if (entityType === 'unstructured' && fileType) {
    const storagePath =
      selectedFile?.fileBlob?.storagePath || selectedFile?.path;

    if (storagePath) {
      entity.filePath = storagePath;
    }
    if (selectedFile?.id) {
      entity.fileEntryId = selectedFile.id;
    }
    entity.fileType = fileType;
    if (selectedFile?.name) {
      entity.fileDisplayName = selectedFile.name;
    }

    // Set default extraction settings based on file type
    const agentType = getDefaultAgentTypeForFileType(fileType);
    const defaults = getDefaults(SERVICES.UNSTRUCTURED_DATA_LOADER, agentType);

    entity.extractionSettings = {
      agent: {
        type: agentType,
        ...defaults,
        extractionPasses: 2,
        targetFormat:
          fileType === 'pdf' || fileType === 'directory' ? 'JPG' : undefined,
      },
      findUnspecifiedRelationships: true,
    };
  }

  return entity;
};

export const createNewProperty = (
  name: string = '',
  type: WorkflowEntityProperty['type'] = 'string',
  sourceField?: string
): WorkflowEntityProperty => {
  return {
    id: generateId(),
    name,
    type,
    sourceField,
    isSearchable: false,
    hasIndex: false,
    isUnique: false,
    encrypt: false,
  };
};

export const createStaticProperty = (
  name: string = 'static_property',
  type: WorkflowEntityProperty['type'] = 'string',
  staticValue: string = ''
): WorkflowEntityProperty => {
  return {
    id: generateId(),
    name,
    type,
    isStatic: true,
    staticValue,
    isSearchable: false,
    hasIndex: false,
    isUnique: false,
    encrypt: false,
  };
};

/**
 * Checks if a property's sourceField or location columns match the available columns
 */
export const propertyMatchesSourceColumns = (
  property: WorkflowEntityProperty,
  availableColumns: string[]
): boolean => {
  // Static properties don't need to match columns
  if (property.isStatic) {
    return true;
  }

  // For location properties, check latitudeColumn and longitudeColumn
  if (property.type === 'location') {
    const hasLatMatch =
      !property.latitudeColumn ||
      availableColumns.includes(property.latitudeColumn);
    const hasLonMatch =
      !property.longitudeColumn ||
      availableColumns.includes(property.longitudeColumn);
    return hasLatMatch && hasLonMatch;
  }

  // For other properties, check sourceField
  if (property.sourceField) {
    return availableColumns.includes(property.sourceField);
  }

  // If no sourceField, it doesn't match (for structured entities)
  return false;
};

/**
 * Gets available columns from entity's source file
 * Returns empty array if source file not found or no metadata
 */
export const getEntitySourceColumns = (
  entity: WorkflowEntity,
  files: WorkflowFile[]
): string[] => {
  // For structured entities, get source from sources array or properties
  if (entity.entityType !== 'unstructured') {
    const sourcePath =
      entity.sources?.[0] || entity.properties.find((p) => p.source)?.source;
    if (sourcePath) {
      const sourceFile = files.find(
        (f) =>
          f.fileBlob?.storagePath === sourcePath || f.id === entity.fileEntryId
      );
      return sourceFile?.fileMetadata?.headers || [];
    }
  } else {
    // For unstructured entities, get source from filePath/filePaths
    const sourcePath = entity.filePaths?.[0] || entity.filePath;
    if (sourcePath) {
      const sourceFile = files.find(
        (f) =>
          f.fileBlob?.storagePath === sourcePath ||
          f.path === sourcePath ||
          f.id === entity.fileEntryId
      );
      return sourceFile?.fileMetadata?.headers || [];
    }
  }

  return [];
};

export const calculateEntityCompletion = (
  entity: WorkflowEntity,
  files: WorkflowFile[] = []
): boolean => {
  const hasName = entity.name.trim().length > 0;
  const hasProperties = entity.properties.length > 0;

  // Use different validation based on entity type
  const allPropertiesValid =
    entity.entityType === 'unstructured'
      ? entity.properties.every(validateUnstructuredEntityProperty)
      : entity.properties.every(validateEntityProperty);

  const hasValidLocationProperties = validateLocationProperties(entity);

  // For structured entities, validate that all properties match source file columns
  let allPropertiesMatchColumns = true;
  if (entity.entityType !== 'unstructured' && files.length > 0) {
    const availableColumns = getEntitySourceColumns(entity, files);
    if (availableColumns.length > 0) {
      allPropertiesMatchColumns = entity.properties.every((prop) =>
        propertyMatchesSourceColumns(prop, availableColumns)
      );
    }
  }

  return (
    hasName &&
    hasProperties &&
    allPropertiesValid &&
    hasValidLocationProperties &&
    allPropertiesMatchColumns
  );
};

export const getEntityCompletionStatus = (
  entity: WorkflowEntity
): { nameDefined: boolean; propertiesMapped: boolean } => {
  return {
    nameDefined: entity.name.trim().length > 0,
    propertiesMapped:
      entity.properties.length > 0 &&
      entity.properties.every(
        entity.entityType === 'unstructured'
          ? validateUnstructuredEntityProperty
          : validateEntityProperty
      ),
  };
};

export const generateWorkflowName = (): string => {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `Workflow ${timestamp}`;
};

export const validateWorkflowForLaunch = (
  state: any
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!state.name.trim()) {
    errors.push('Workflow name is required');
  }

  if (state.files.length === 0) {
    errors.push('At least one file must be uploaded');
  }

  if (state.entities.length === 0) {
    errors.push('At least one entity must be created');
  }

  const incompleteEntities = state.entities.filter(
    (entity: WorkflowEntity) => !validateEntity(entity)
  );
  if (incompleteEntities.length > 0) {
    errors.push(`${incompleteEntities.length} entity(ies) are incomplete`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const inferDataType = (header: string): string => {
  const lowerHeader = header.toLowerCase();
  if (lowerHeader.includes('datetime') || lowerHeader.includes('time')) {
    return 'datetime';
  }
  if (
    lowerHeader.includes('id') ||
    lowerHeader.includes('count') ||
    lowerHeader.includes('number')
  ) {
    return 'number';
  }
  if (
    lowerHeader.includes('is') ||
    lowerHeader.includes('has') ||
    lowerHeader.includes('active')
  ) {
    return 'boolean';
  }
  return 'string';
};

// Reserved property names that cannot be used directly
const RESERVED_PROPERTY_NAMES = [
  'id',
  'file_path',
  'process_id',
  'workflow_id',
];

/**
 * Generates a safe property name by checking if the proposed name is reserved
 * If it's reserved, prefixes it with the entity name and an underscore
 * @param proposedName - The proposed property name
 * @param entityName - The name of the entity
 * @returns A safe property name that doesn't conflict with reserved names
 */
/**
 * Generates a unique entity name by checking against existing entities
 * If the name already exists, appends a consecutive number
 * @param proposedName - The proposed entity name
 * @param existingEntities - Array of existing entities to check against
 * @returns A unique entity name
 */
export const generateUniqueEntityName = (
  proposedName: string,
  existingEntities: WorkflowEntity[]
): string => {
  const baseName = proposedName.trim();
  const existingNames = existingEntities.map((e) => e.name.toLowerCase());

  if (!existingNames.includes(baseName.toLowerCase())) {
    return baseName;
  }

  let counter = 1;
  let uniqueName = `${baseName}${counter}`;

  while (existingNames.includes(uniqueName.toLowerCase())) {
    counter++;
    uniqueName = `${baseName}${counter}`;
  }

  return uniqueName;
};

export const generateSafePropertyName = (
  proposedName: string,
  entityName: string
): string => {
  const normalizedName = proposedName.toLowerCase().replace(/\s+/g, '_');

  // Check if the proposed name is reserved
  if (RESERVED_PROPERTY_NAMES.includes(normalizedName)) {
    // Prefix with entity name and underscore
    const entityPrefix = entityName.toLowerCase().replace(/\s+/g, '_');
    return `${entityPrefix}_${normalizedName}`;
  }

  return normalizedName;
};

/**
 * Checks if a property name is reserved
 * @param propertyName - The property name to check
 * @returns True if the property name is reserved
 */
export const isReservedPropertyName = (propertyName: string): boolean => {
  const normalizedName = propertyName.toLowerCase().replace(/\s+/g, '_');
  return RESERVED_PROPERTY_NAMES.includes(normalizedName);
};

/**
 * Calculate edge offset to avoid overlapping when multiple edges exist between same nodes
 * @param sourceId - Source node ID
 * @param targetId - Target node ID
 * @param edgeIndex - Index of the edge among all edges between these nodes
 * @param totalEdges - Total number of edges between these nodes
 * @param offsetDistance - Distance to offset edges (default: 20)
 * @returns Object with offsetX and offsetY for the edge
 */
export const calculateEdgeOffset = (
  _sourceId: string,
  _targetId: string,
  edgeIndex: number,
  totalEdges: number,
  offsetDistance: number = 20
): { offsetX: number; offsetY: number } => {
  if (totalEdges <= 1) {
    return { offsetX: 0, offsetY: 0 };
  }

  // Calculate offset based on edge index
  const isEven = edgeIndex % 2 === 0;
  const offsetIndex = Math.floor(edgeIndex / 2);

  // Alternate between positive and negative offsets
  const baseOffset = (offsetIndex + 1) * offsetDistance;
  const offset = isEven ? baseOffset : -baseOffset;

  // For horizontal connections, offset vertically
  // For vertical connections, offset horizontally
  // We'll assume horizontal connections for now (can be enhanced later)
  return { offsetX: 0, offsetY: offset };
};

/**
 * Transform WorkflowExecution to WorkflowBuilderState
 * This function converts the API response format back to the UI state format
 */
export const transformWorkflowExecutionToState = (
  workflowExecution: WorkflowExecution
): WorkflowBuilderState => {
  const entities: WorkflowEntity[] = [];
  const relationships: WorkflowRelationship[] = [];
  const relationshipMappings: WorkflowRelationshipMapping[] = [];
  const unstructuredRelationships: UnstructuredRelationship[] = [];
  const linkNodesConfigurations: LinkNodesStep[] = [];
  const enrichRules: EnrichRule[] = [];
  const cypherClassifierSteps: CypherClassifierStep[] = [];

  // Extract step options from the first structured and unstructured steps
  const structuredStep = workflowExecution.workflow.steps.find(
    (step) => step.type === 'IngestStructured'
  );
  const unstructuredStep = workflowExecution.workflow.steps.find(
    (step) => step.type === 'IngestUnstructured'
  );

  const stepOptions = {
    structured: {
      mergeNodes: structuredStep?.options?.mergeNodes ?? true,
      onDuplicate: structuredStep?.options?.onDuplicate ?? 'update',
      batchSize: structuredStep?.options?.batchSize ?? 0,
      vectorize: structuredStep?.options?.vectorize ?? false,
      encoding: structuredStep?.options?.encoding ?? 'utf-8',
    },
    unstructured: {
      mergeNodes: unstructuredStep?.options?.mergeNodes ?? true,
      onDuplicate: unstructuredStep?.options?.onDuplicate ?? 'update',
      batchSize: unstructuredStep?.options?.batchSize ?? 0,
      vectorize: unstructuredStep?.options?.vectorize ?? true,
      encoding: unstructuredStep?.options?.encoding ?? 'utf-8',
    },
  };

  function convertStaticValue(propValue: any) {
    // Always return as string to maintain UI compatibility
    // The UI expects string values for form inputs
    if (!propValue || !propValue.value) return '';

    switch (propValue.type) {
      case 'int':
      case 'float':
        return String(+(propValue as any).value);
      case 'boolean':
        return String(
          (propValue as any).value === 'true' ||
            (propValue as any).value === '1'
        );
      default:
        return String((propValue as any).value);
    }
  }

  // Process IngestStructured step to extract entities and relationships
  if (structuredStep) {
    // Extract entities from schema and file mappings
    const entityMap = new Map<string, WorkflowEntity>();

    // Process indexes to identify entities
    structuredStep.schema?.indexes?.forEach((index: any) => {
      // Handle individual entity indexes
      if (index.label && !entityMap.has(index.label)) {
        const entity: WorkflowEntity = {
          id: `entity-${index.label}-${Date.now()}`,
          name: index.label,
          properties: [],
          sources: [],
          isComplete: false,
          color: '#3B82F6',
        };
        entityMap.set(index.label, entity);
      }

      // Handle consolidated fulltext indexes with multiple labels
      if (index.labels && Array.isArray(index.labels)) {
        index.labels.forEach((label: string) => {
          if (!entityMap.has(label)) {
            const entity: WorkflowEntity = {
              id: `entity-${label}-${Date.now()}`,
              name: label,
              properties: [],
              sources: [],
              isComplete: false,
              color: '#3B82F6',
            };
            entityMap.set(label, entity);
          }
        });
      }
    });

    // Process constraints to identify entities
    structuredStep.schema?.constraints?.forEach((constraint: any) => {
      if (constraint.label && !entityMap.has(constraint.label)) {
        const entity: WorkflowEntity = {
          id: `entity-${constraint.label}-${Date.now()}`,
          name: constraint.label,
          properties: [],
          sources: [],
          isComplete: false,
          color: '#3B82F6',
        };
        entityMap.set(constraint.label, entity);
      }
    });

    // Process file mappings to extract properties and sources
    structuredStep.files?.forEach((file: any) => {
      file.mappings?.forEach((mapping: any) => {
        const entityName = mapping.label;
        let entity = entityMap.get(entityName);

        if (!entity) {
          entity = {
            id: `entity-${entityName}-${Date.now()}`,
            name: entityName,
            properties: [],
            sources: [],
            isComplete: false,
            color: '#3B82F6',
          };
          entityMap.set(entityName, entity);
        }

        // Extract properties from mapping
        Object.entries(mapping.properties || {}).forEach(
          ([propName, propValue]) => {
            const property: any = {
              id: `prop-${propName}-${Date.now()}`,
              name: propName,
              type:
                typeof propValue === 'string'
                  ? 'string'
                  : (propValue as any).type || 'string',
              sourceField:
                typeof propValue === 'string'
                  ? propValue
                  : (propValue as any).column || propName,
              isSearchable: false,
              hasIndex: false,
              isUnique: false,
              source: file.path,
              encrypt:
                typeof propValue === 'object' && (propValue as any).encrypt
                  ? true
                  : false,
            };

            // Handle geo properties - convert to Location type
            if (
              typeof propValue === 'object' &&
              (propValue as any).type === 'geo'
            ) {
              const geoProp = propValue as any;
              const latColumn = geoProp.latColumn;
              const lonColumn = geoProp.lonColumn;

              // Create location property
              const locationProperty: any = {
                id: `prop-${propName}-${Date.now()}`,
                name: propName,
                type: 'location',
                latitudeColumn: latColumn,
                longitudeColumn: lonColumn,
                isSearchable: false,
                hasIndex: false,
                isUnique: false,
                source: file.path,
              };

              // Check if this property has an index
              const individualEntityIndexes =
                structuredStep.schema?.indexes?.filter(
                  (idx: any) =>
                    idx.label === entityName &&
                    idx.properties?.includes(propName)
                ) || [];

              if (individualEntityIndexes.length > 0) {
                locationProperty.hasIndex = true;
              }

              // Check if this property has a unique constraint
              const entityConstraints =
                structuredStep.schema?.constraints?.filter(
                  (constraint: any) =>
                    constraint.label === entityName &&
                    constraint.properties?.includes(propName)
                ) || [];

              if (entityConstraints.length > 0) {
                locationProperty.isUnique = true;
              }

              // Check if this property has a fulltext index
              const individualFulltextIndexes = individualEntityIndexes.filter(
                (idx: any) => idx.type === 'fulltext'
              );
              const consolidatedFulltextIndexes =
                structuredStep.schema?.indexes?.filter(
                  (idx: any) =>
                    idx.type === 'fulltext' &&
                    idx.labels &&
                    idx.labels.includes(entityName) &&
                    idx.properties?.includes(propName)
                ) || [];

              if (
                individualFulltextIndexes.length > 0 ||
                consolidatedFulltextIndexes.length > 0
              ) {
                locationProperty.isSearchable = true;
              }

              // Add location property to the entity
              entity.properties.push(locationProperty);

              // Skip adding the original property since we've converted it
              return;
            }

            // Check if this property has an index
            // For default indexes, ONLY look at individual entity indexes
            const individualEntityIndexes =
              structuredStep.schema?.indexes?.filter(
                (idx: any) =>
                  idx.label === entityName && idx.properties?.includes(propName)
              ) || [];

            if (individualEntityIndexes.length > 0) {
              property.hasIndex = true;
            }

            // Check if this property has a unique constraint
            const entityConstraints =
              structuredStep.schema?.constraints?.filter(
                (constraint: any) =>
                  constraint.label === entityName &&
                  constraint.properties?.includes(propName)
              ) || [];

            if (entityConstraints.length > 0) {
              property.isUnique = true;
            }

            // Check if this property has a fulltext index
            // Look for both individual entity indexes and consolidated fulltext indexes
            const individualFulltextIndexes = individualEntityIndexes.filter(
              (idx: any) => idx.type === 'fulltext'
            );
            const consolidatedFulltextIndexes =
              structuredStep.schema?.indexes?.filter(
                (idx: any) =>
                  idx.type === 'fulltext' &&
                  idx.labels &&
                  idx.labels.includes(entityName) &&
                  idx.properties?.includes(propName)
              ) || [];

            if (
              individualFulltextIndexes.length > 0 ||
              consolidatedFulltextIndexes.length > 0
            ) {
              property.isSearchable = true;
            }

            entity.properties.push(property);
          }
        );

        // Extract static properties
        Object.entries(mapping.staticProperties || {}).forEach(
          ([propName, propValue]: [string, any]) => {
            const property: any = {
              id: `prop-${propName}-${Date.now()}`,
              name: propName,
              type: propValue.type || 'string',
              isStatic: true,
              staticValue: convertStaticValue(propValue),
              isSearchable: false,
              hasIndex: false,
              isUnique: false,
            };

            // Check if this static property has an index
            // For default indexes, ONLY look at individual entity indexes
            const individualEntityIndexes =
              structuredStep.schema?.indexes?.filter(
                (idx: any) =>
                  idx.label === entityName && idx.properties?.includes(propName)
              ) || [];

            if (individualEntityIndexes.length > 0) {
              property.hasIndex = true;
            }

            // Check if this static property has a unique constraint
            const entityConstraints =
              structuredStep.schema?.constraints?.filter(
                (constraint: any) =>
                  constraint.label === entityName &&
                  constraint.properties?.includes(propName)
              ) || [];

            if (entityConstraints.length > 0) {
              property.isUnique = true;
            }

            // Check if this static property has a fulltext index
            // Look for both individual entity indexes and consolidated fulltext indexes
            const individualFulltextIndexes = individualEntityIndexes.filter(
              (idx: any) => idx.type === 'fulltext'
            );
            const consolidatedFulltextIndexes =
              structuredStep.schema?.indexes?.filter(
                (idx: any) =>
                  idx.type === 'fulltext' &&
                  idx.labels &&
                  idx.labels.includes(entityName) &&
                  idx.properties?.includes(propName)
              ) || [];

            if (
              individualFulltextIndexes.length > 0 ||
              consolidatedFulltextIndexes.length > 0
            ) {
              property.isSearchable = true;
            }

            entity.properties.push(property);
          }
        );

        // Add source file ID and fileEntryId
        if (file.path && !entity.sources.includes(file.path)) {
          entity.sources.push(file.path);
        }
        // Set fileEntryId from the file object
        if (file.fileEntryId) {
          entity.fileEntryId = file.fileEntryId;
        }
      });
    });

    // Extract relationships from structured step
    structuredStep.relationships?.forEach((rel: any) => {
      const relationshipMapping: WorkflowRelationshipMapping = {
        id: `rel-${rel.name}-${Date.now()}`,
        name: rel.name,
        startLabel: rel.startLabel,
        startNodeProperty: rel.startNodeProperty || 'id',
        startSourceField: rel.startSourceField,
        endLabel: rel.endLabel,
        endNodeProperty: rel.endNodeProperty || 'id',
        endSourceField: rel.endSourceField,
        properties: [],
        file: rel.path,
        fileEntryId: rel.fileEntryId,
      };

      // Extract relationship properties
      Object.entries(rel.properties || {}).forEach(
        ([propName, propValue]: [string, any]) => {
          relationshipMapping.properties.push({
            id: `rel-prop-${propName}-${Date.now()}`,
            name: propName,
            type:
              typeof propValue === 'string'
                ? 'string'
                : (propValue as any).type || 'string',
            sourceField:
              typeof propValue === 'string'
                ? propValue
                : (propValue as any).column || propName,
          });
        }
      );

      // Extract static relationship properties
      Object.entries(rel.staticProperties || {}).forEach(
        ([propName, propValue]: [string, any]) => {
          relationshipMapping.properties.push({
            id: `rel-prop-${propName}-${Date.now()}`,
            name: propName,
            type: (propValue as any).type || 'string',
            isStatic: true,
            staticValue: convertStaticValue(propValue),
          });
        }
      );

      relationshipMappings.push(relationshipMapping);
    });

    // Add entities to the list
    entities.push(...Array.from(entityMap.values()));
  }

  // Process IngestUnstructured step to extract entities
  if (unstructuredStep) {
    unstructuredStep.files?.forEach((file: any) => {
      file.nodes?.forEach((node: any) => {
        const agentType = extractionAgentTypeFromApi(
          file.extractionRule?.agent?.type,
          AGENT_TYPES.LANGCHAIN
        );
        const entity: WorkflowEntity = {
          id: `entity-${node.label}-${Date.now()}`,
          name: node.label,
          entityType: 'unstructured',
          filePath: file.path,
          fileEntryId: file.fileEntryId || '',
          fileType: file.type || 'file',
          directoryFileTypes: file.fileTypes,
          properties: [],
          sources: [file.path],
          isComplete: false,
          color: '#3B82F6',
          extractInstructions: Array.isArray(node.extractInstructions)
            ? node.extractInstructions
            : node.extractInstructions
            ? [String(node.extractInstructions)]
            : [],
          extractionSettings: {
            agent: {
              type: agentType,
              modelName:
                file.extractionRule?.agent?.modelName ||
                getDefaults(
                  SERVICES.UNSTRUCTURED_DATA_LOADER,
                  agentType
                ).modelName,
              modelVersion:
                file.extractionRule?.agent?.modelVersion ||
                getDefaults(
                  SERVICES.UNSTRUCTURED_DATA_LOADER,
                  agentType
                ).modelVersion,
              extractionPasses:
                file.extractionRule?.agent?.extractionPasses || 2,
              targetFormat: file.extractionRule?.agent?.targetFormat,
              prompt: file.extractionRule?.agent?.prompt,
            },
            findUnspecifiedRelationships:
              file.extractionRule?.findUnspecifiedRelationships || false,
            knownEntities: file.extractionRule?.knownEntities || {},
            filePages: file.extractionRule?.filePages,
          },
          labelConstraints: node.labelConstrains || [],
        };

        // Extract properties
        node.properties?.forEach((prop: any) => {
          // Handle geo properties - convert to Location type
          if (typeof prop === 'object' && prop.type === 'geo') {
            const geoProp = prop;
            const latProperty = geoProp.latProperty;
            const lonProperty = geoProp.lonProperty;
            const latColumn = geoProp.latColumn;
            const lonColumn = geoProp.lonColumn;

            // Create location property
            const locationProperty: any = {
              id: `prop-${prop.property}-${Date.now()}`,
              name: prop.property,
              type: 'location',
              latitudeProperty: latProperty,
              longitudeProperty: lonProperty,
              latitudeColumn: latColumn,
              longitudeColumn: lonColumn,
              required: prop.required || false,
              isSearchable: false,
              hasIndex: false,
              isUnique: false,
              encrypt: prop.encrypt || false,
            };

            // Check if this property has an index in unstructured step schema
            if (unstructuredStep.schema?.indexes) {
              // Check individual entity indexes for default indexes
              const individualIndexes = unstructuredStep.schema.indexes.filter(
                (idx: any) =>
                  idx.label === entity.name &&
                  idx.properties?.includes(prop.property)
              );

              if (individualIndexes.length > 0) {
                locationProperty.hasIndex = true;
              }

              // Check consolidated fulltext indexes for searchable properties
              const consolidatedFulltextIndexes =
                unstructuredStep.schema.indexes.filter(
                  (idx: any) =>
                    idx.type === 'fulltext' &&
                    idx.labels &&
                    idx.labels.includes(entity.name) &&
                    idx.properties?.includes(prop.property)
                );

              if (consolidatedFulltextIndexes.length > 0) {
                locationProperty.isSearchable = true;
              }
            }

            entity.properties.push(locationProperty);
            return;
          }

          const property: any = {
            id: `prop-${prop.property}-${Date.now()}`,
            name: prop.property,
            type: prop.type || 'string',
            required: prop.required || false,
            isSearchable: false,
            hasIndex: false,
            isUnique: false,
            encrypt: prop.encrypt || false,
          };

          // Check if this property has an index in unstructured step schema
          if (unstructuredStep.schema?.indexes) {
            // Check individual entity indexes for default indexes
            const individualIndexes = unstructuredStep.schema.indexes.filter(
              (idx: any) =>
                idx.label === entity.name &&
                idx.properties?.includes(prop.property)
            );

            if (individualIndexes.length > 0) {
              property.hasIndex = true;
            }

            // Check consolidated fulltext indexes for searchable properties
            const consolidatedFulltextIndexes =
              unstructuredStep.schema.indexes.filter(
                (idx: any) =>
                  idx.type === 'fulltext' &&
                  idx.labels &&
                  idx.labels.includes(entity.name) &&
                  idx.properties?.includes(prop.property)
              );

            if (consolidatedFulltextIndexes.length > 0) {
              property.isSearchable = true;
            }
          }

          entity.properties.push(property);
        });

        // Extract static properties
        Object.entries(node.staticProperties || {}).forEach(
          ([propName, propValue]: [string, any]) => {
            const property: any = {
              id: `prop-${propName}-${Date.now()}`,
              name: propName,
              type: (propValue as any).type || 'string',
              isStatic: true,
              staticValue: convertStaticValue(propValue),
              isSearchable: false,
              hasIndex: false,
              isUnique: false,
            };

            // Check if this static property has an index in unstructured step schema
            if (unstructuredStep.schema?.indexes) {
              // Check individual entity indexes for default indexes
              const individualIndexes = unstructuredStep.schema.indexes.filter(
                (idx: any) =>
                  idx.label === entity.name &&
                  idx.properties?.includes(propName)
              );

              if (individualIndexes.length > 0) {
                property.hasIndex = true;
              }

              // Check consolidated fulltext indexes for searchable properties
              const consolidatedFulltextIndexes =
                unstructuredStep.schema.indexes.filter(
                  (idx: any) =>
                    idx.type === 'fulltext' &&
                    idx.labels &&
                    idx.labels.includes(entity.name) &&
                    idx.properties?.includes(propName)
                );

              if (consolidatedFulltextIndexes.length > 0) {
                property.isSearchable = true;
              }
            }

            entity.properties.push(property);
          }
        );

        entities.push(entity);
      });

      // Extract unstructured relationships from this file
      file.relationships?.forEach((rel: any) => {
        const unstructuredRelationship: UnstructuredRelationship = {
          id: `unstructured-rel-${rel.name}-${Date.now()}`,
          name: rel.name,
          startLabel: rel.startLabel,
          endLabel: rel.endLabel,
          filePath: file.path,
          staticProperties: rel.staticProperties || {},
        };
        unstructuredRelationships.push(unstructuredRelationship);
      });
    });
  }

  // Process LinkNodes steps
  workflowExecution.workflow.steps?.forEach((step: any) => {
    if (step.type === 'LinkNodes') {
      // Process linkRules to add strategy with method 'none' when no strategy exists
      const processedLinkRules = (step.link.linkRules || []).map(
        (rule: any) => {
          if (!rule.strategy) {
            return {
              ...rule,
              strategy: { method: 'none' },
            };
          }
          return rule;
        }
      );

      // Process pipeline strategies to ensure all required fields are included
      const processedPipeline = (step.link.pipeline || []).map(
        (strategy: any) => {
          const processedStrategy: any = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            method: strategy.method,
          };

          // Add method-specific required fields
          switch (strategy.method) {
            case LINKING_STRATEGY_TYPES.EXACT_MATCH:
              processedStrategy.threshold =
                strategy.threshold !== undefined ? strategy.threshold : 1.0;
              break;
            case LINKING_STRATEGY_TYPES.JACCARD_SIMILARITY:
              processedStrategy.threshold =
                strategy.threshold !== undefined ? strategy.threshold : 0.8;
              if (strategy.token_separator) {
                processedStrategy.token_separator = strategy.token_separator;
              }
              break;
            case LINKING_STRATEGY_TYPES.EMBEDDING_SIMILARITY:
              processedStrategy.threshold =
                strategy.threshold !== undefined ? strategy.threshold : 0.8;
              processedStrategy.minhashThreshold =
                strategy.minhashThreshold !== undefined
                  ? strategy.minhashThreshold
                  : 0.25;
              processedStrategy.agent = {
                type:
                  strategy.agent?.type ?? EMBEDDINGS_API_TYPES.EMBEDDINGS_API,
                model: strategy.agent?.model ?? embeddingModels[0].value,
                dimensions: strategy.agent?.dimensions ?? 3072,
              };
              break;
            case LINKING_STRATEGY_TYPES.LLM:
              processedStrategy.minhashThreshold =
                strategy.minhashThreshold !== undefined
                  ? strategy.minhashThreshold
                  : 0.25;
              processedStrategy.agent = {
                type: strategy.agent?.type ?? linkingDefaults.type,
                modelName:
                  strategy.agent?.modelName ?? linkingDefaults.modelName,
                temperature: strategy.agent?.temperature ?? 0.0,
              };
              // Only add instructions if they exist
              if (strategy.agent?.instructions) {
                processedStrategy.agent.instructions =
                  strategy.agent.instructions;
              }
              break;
          }

          return processedStrategy;
        }
      );

      const linkNodesConfig: LinkNodesStep = {
        id: `link-${step.link.name}-${Date.now()}`,
        type: 'LinkNodes',
        link: {
          name: step.link.name,
          source: {
            label: step.link.source.label,
            alias: step.link.source.alias,
            filter: step.link.source.filter,
          },
          destination: {
            label: step.link.destination.label,
            alias: step.link.destination.alias,
            filter: step.link.destination.filter,
          },
          pipeline: processedPipeline,
          linkRules: processedLinkRules,
        },
      };
      linkNodesConfigurations.push(linkNodesConfig);
    }
  });

  // Process Enrich steps
  workflowExecution.workflow.steps?.forEach((step: any) => {
    if (step.type === 'Enrich') {
      enrichRules.push(step.rule);
    }
  });

  // Process CypherClassifier steps — only include keys that exist on the
  // API response so rule-ref vs inline payloads stay distinct when re-serialized.
  workflowExecution.workflow.steps?.forEach((step: any) => {
    if (step.type === 'CypherClassifier') {
      const entry: CypherClassifierStep = {
        type: 'CypherClassifier',
        graphFilter: step.graphFilter ?? { labelFilters: [], propertyFilters: [] },
      };
      if (step.ruleId != null) entry.ruleId = step.ruleId;
      if (step.destinationProperty != null) entry.destinationProperty = step.destinationProperty;
      if (step.expressionCypher != null) entry.expressionCypher = step.expressionCypher;
      if (step.parameters != null) entry.parameters = step.parameters;
      if (step.outputType != null) entry.outputType = step.outputType;
      if (step.writeMode != null) entry.writeMode = step.writeMode;
      if (step.vectorize != null) entry.vectorize = step.vectorize;
      cypherClassifierSteps.push(entry);
    }
  });

  return {
    id: workflowExecution.id,
    name: `Workflow ${workflowExecution.id}`, // Generate a name since it's not in the API response
    description: '', // No description in the API response
    stepOptions,
    entities,
    relationships,
    relationshipMappings,
    unstructuredRelationships,
    linkNodesConfigurations,
    enrichRules,
    cypherClassifierSteps,
    currentStep: workflowExecution.step || 0,
    isComplete: workflowExecution.step >= 3, // Assuming 3 steps: prepare, model, final
  };
};

/**
 * Check if there are unsaved changes in step options (for Final Review → Model Designer)
 */
export const hasUnsavedStepOptions = (
  originalApiData: any,
  currentStepOptions: {
    structured?: any;
    unstructured?: any;
  }
): boolean => {
  if (!originalApiData || !currentStepOptions) return false;

  const structuredStep = originalApiData.workflow.steps.find(
    (step: any) => step.type === 'IngestStructured'
  );
  const unstructuredStep = originalApiData.workflow.steps.find(
    (step: any) => step.type === 'IngestUnstructured'
  );

  const hasStructuredChanges =
    currentStepOptions.structured &&
    (currentStepOptions.structured.mergeNodes !==
      (structuredStep?.options?.mergeNodes ?? true) ||
      currentStepOptions.structured.onDuplicate !==
        (structuredStep?.options?.onDuplicate ?? 'update') ||
      currentStepOptions.structured.batchSize !==
        (structuredStep?.options?.batchSize ?? 0) ||
      currentStepOptions.structured.vectorize !==
        (structuredStep?.options?.vectorize ?? false) ||
      currentStepOptions.structured.encoding !==
        (structuredStep?.options?.encoding ?? 'utf-8'));

  const hasUnstructuredChanges =
    currentStepOptions.unstructured &&
    (currentStepOptions.unstructured.mergeNodes !==
      (unstructuredStep?.options?.mergeNodes ?? true) ||
      currentStepOptions.unstructured.onDuplicate !==
        (unstructuredStep?.options?.onDuplicate ?? 'update') ||
      currentStepOptions.unstructured.batchSize !==
        (unstructuredStep?.options?.batchSize ?? 0) ||
      currentStepOptions.unstructured.vectorize !==
        (unstructuredStep?.options?.vectorize ?? true) ||
      currentStepOptions.unstructured.encoding !==
        (unstructuredStep?.options?.encoding ?? 'utf-8'));

  return hasStructuredChanges || hasUnstructuredChanges;
};

/**
 * Check if there are unsaved changes in workflow components (for Model Designer → Prepare Data)
 */
export const hasUnsavedWorkflowComponents = (
  originalApiData: any,
  generateWorkflowConfig: () => any
): boolean => {
  if (!originalApiData) return false;

  // Helper function to compare objects deeply
  const hasChanges = (
    current: any,
    original: any,
    path: string = ''
  ): boolean => {
    if (!current || !original) {
      if (current !== original) {
        console.log(`🔍 [${path}] Different values:`, { current, original });
        return true;
      }
      return false;
    }

    // Check if arrays have different lengths
    if (Array.isArray(current) && Array.isArray(original)) {
      if (current.length !== original.length) {
        console.log(`🔍 [${path}] Array length different:`, {
          currentLength: current.length,
          originalLength: original.length,
        });
        return true;
      }

      // Check each item for changes
      const hasArrayChanges = current.some((item, index) =>
        hasChanges(item, original[index], `${path}[${index}]`)
      );
      if (hasArrayChanges) {
        console.log(`🔍 [${path}] Array items have changes`);
      }
      return hasArrayChanges;
    }

    // Check if objects have different properties
    if (typeof current === 'object' && typeof original === 'object') {
      const currentKeys = Object.keys(current);
      const originalKeys = Object.keys(original);

      // Only compare keys that both objects have
      const sharedKeys = currentKeys.filter((key) =>
        originalKeys.includes(key)
      );

      // Check each shared property for changes
      const hasObjectChanges = sharedKeys.some((key) =>
        hasChanges(current[key], original[key], `${path}.${key}`)
      );
      if (hasObjectChanges) {
        console.log(`🔍 [${path}] Object properties have changes`);
      }
      return hasObjectChanges;
    }

    // Compare primitive values
    if (current !== original) {
      console.log(`🔍 [${path}] Primitive values different:`, {
        current,
        original,
        currentType: typeof current,
        originalType: typeof original,
      });
      return true;
    }

    return false;
  };

  try {
    // Generate current workflow config to get the same structure as API response
    const currentWorkflowConfig = generateWorkflowConfig();

    // Normalize both current and original to ensure extractInstructions exists where expected
    const normalizeExtractInstructions = (config: any) => {
      if (!config?.steps) return;
      config.steps
        .filter((s: any) => s.type === 'IngestUnstructured')
        .forEach((step: any) => {
          (step.files || []).forEach((file: any) => {
            (file.nodes || []).forEach((node: any) => {
              if (!Array.isArray(node.extractInstructions)) {
                node.extractInstructions = node.extractInstructions
                  ? [String(node.extractInstructions)]
                  : [];
              }
            });
          });
        });
    };

    // Deep clone originals to avoid mutating inputs
    const originalClone = JSON.parse(JSON.stringify(originalApiData));
    const currentClone = JSON.parse(JSON.stringify(currentWorkflowConfig));

    normalizeExtractInstructions(originalClone);
    normalizeExtractInstructions(currentClone);

    // Compare the workflow steps directly
    console.log('🔍 Comparing workflow steps for unsaved changes...');

    const hasStructuredChanges = hasChanges(
      currentClone.steps?.find((s: any) => s.type === 'IngestStructured'),
      originalClone.workflow.steps?.find(
        (s: any) => s.type === 'IngestStructured'
      ),
      'IngestStructured'
    );

    const hasUnstructuredChanges = hasChanges(
      currentClone.steps?.find((s: any) => s.type === 'IngestUnstructured'),
      originalClone.workflow.steps?.find(
        (s: any) => s.type === 'IngestUnstructured'
      ),
      'IngestUnstructured'
    );

    const hasLinkNodesChanges = hasChanges(
      currentClone.steps?.filter((s: any) => s.type === 'LinkNodes'),
      originalClone.workflow.steps?.filter((s: any) => s.type === 'LinkNodes'),
      'LinkNodes'
    );

    const hasEnrichChanges = hasChanges(
      currentClone.steps?.filter((s: any) => s.type === 'Enrich'),
      originalClone.workflow.steps?.filter((s: any) => s.type === 'Enrich'),
      'Enrich'
    );

    const hasCypherClassifierChanges = hasChanges(
      currentClone.steps?.filter((s: any) => s.type === 'CypherClassifier'),
      originalClone.workflow.steps?.filter((s: any) => s.type === 'CypherClassifier'),
      'CypherClassifier'
    );

    return (
      hasStructuredChanges ||
      hasUnstructuredChanges ||
      hasLinkNodesChanges ||
      hasEnrichChanges ||
      hasCypherClassifierChanges
    );
  } catch (error) {
    console.error('Error generating workflow config for comparison:', error);
    return false;
  }
};

/**
 * Extracts error message from API error response
 * Handles various error response formats from the backend
 */
export const extractApiErrorMessage = (error: unknown): string => {
  const defaultMessage = 'An unexpected error occurred';
  
  if (error && typeof error === 'object' && 'response' in error) {
    const apiError = error as { response?: { data?: { message?: string; failReason?: string } | string } };
    if (apiError.response?.data) {
      if (typeof apiError.response.data === 'object') {
        if (apiError.response.data.message) {
          return apiError.response.data.message;
        } else if (apiError.response.data.failReason) {
          return apiError.response.data.failReason;
        }
      } else if (typeof apiError.response.data === 'string') {
        return apiError.response.data;
      }
    }
  } else if (error instanceof Error) {
    return error.message;
  }
  
  return defaultMessage;
};

// Re-export createWaitWithAbort from common.helpers for backward compatibility
export { createWaitWithAbort } from './common.helpers';

/**
 * Creates a timeout checker function
 * @param startTime - The start time of the operation (Date.now())
 * @param maxDuration - Maximum duration in milliseconds
 * @param onTimeout - Callback to execute when timeout is reached (only called once)
 * @returns A function that checks if timeout has been reached
 */
export const createTimeoutChecker = (
  startTime: number,
  maxDuration: number,
  onTimeout: () => void
) => {
  let timeoutTriggered = false;
  
  return (): boolean => {
    if (Date.now() - startTime > maxDuration) {
      if (!timeoutTriggered) {
        onTimeout();
        timeoutTriggered = true;
      }
      return true;
    }
    return false;
  };
};

/**
 * Creates a polling status fetcher function
 * @param fetchFn - Function to fetch the workflow status
 * @param onUpdate - Callback to execute when workflow is updated
 * @returns A function that fetches and updates workflow status, returning last known state on error
 */
export const createPollingStatusFetcher = <T>(
  fetchFn: () => Promise<T>,
  onUpdate: (data: T) => void
) => {
  return async (lastKnownData: T): Promise<T> => {
    try {
      const updatedData = await fetchFn();
      onUpdate(updatedData);
      return updatedData;
    } catch (err) {
      console.error('Error fetching status during polling:', err);
      // Return the last known state if fetch fails
      return lastKnownData;
    }
  };
};

/**
 * Creates a robust polling status fetcher with circuit breaker pattern
 * Tracks consecutive errors and aborts after MAX_CONSECUTIVE_ERRORS
 * Differentiates between server errors (5xx) and client errors (4xx)
 */
export const createRobustPollingStatusFetcher = <T>(
  fetchFn: () => Promise<T>,
  onUpdate: (data: T) => void,
  onError?: (error: Error, consecutiveErrors: number) => void,
  maxConsecutiveErrors: number = 3
) => {
  let consecutiveErrors = 0;

  return async (lastKnownData: T): Promise<T> => {
    try {
      const updatedData = await fetchFn();
      consecutiveErrors = 0; // Reset on success
      onUpdate(updatedData);
      return updatedData;
    } catch (err) {
      consecutiveErrors++;

      // Check if this is a server error (5xx) - indicates workers are down
      const isServerError =
        err &&
        typeof err === 'object' &&
        'response' in err
          ? (err as any).response?.status >= 500
          : false;

      // Check if this is a client error (4xx) - workflow doesn't exist
      const isClientError =
        err &&
        typeof err === 'object' &&
        'response' in err
          ? (err as any).response?.status >= 400 &&
            (err as any).response?.status < 500
          : false;

      // Abort immediately on 404 (workflow not found) or 4xx client errors
      if (isClientError && (err as any).response?.status === 404) {
        throw new Error('Workflow not found');
      }

      // After MAX_CONSECUTIVE_ERRORS, throw to abort polling
      if (consecutiveErrors >= maxConsecutiveErrors) {
        const errorMessage = isServerError
          ? 'Workflow service is unavailable. Please check if workers are running.'
          : 'Unable to fetch workflow status. Please try again later.';
        onError?.(err as Error, consecutiveErrors);
        throw new Error(errorMessage);
      }

      // Log error but continue with last known state
      console.warn(
        `Polling error (${consecutiveErrors}/${maxConsecutiveErrors}):`,
        err
      );
      onError?.(err as Error, consecutiveErrors);

      return lastKnownData;
    }
  };
};
